import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export default function TableForm({
  table,
  sections,
  onSave,
  onClose,
  serverError = null,
  onClearServerError,
  existingTables = [],
}) {
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    number: '',
    section: sections[0]?.name || '',
    sectionId: sections[0]?.id || '',
    capacity: '',
    description: '',
    isActive: true,
    tableShape: 'square', 
    position: { x: 0, y: 0 } 
  });

  useEffect(() => {
    if (table && sections.length > 0) {
      const matchedSection =
        sections.find(s => s.id === table.idSection || s.id === table.sectionId) ||
        sections.find(s => s.name === table.section);

      setFormData({
        number: table.tableNumber || table.number || '',
        section: matchedSection ? matchedSection.name : sections[0]?.name || '',
        sectionId: matchedSection ? matchedSection.id : sections[0]?.id || '',
        capacity: table.seatingCapacity?.toString() || table.capacity?.toString() || '',
        description: table.description || '',
        isActive: table.isActive !== undefined ? table.isActive : true,
        tableShape: table.tableShape || 'square',
        position: table.position || { x: 0, y: 0 }
      });
    } else if (!table && sections.length > 0) {
      setFormData(prev => ({
        ...prev,
        section: sections[0].name,
        sectionId: sections[0].id
      }));
    }
  }, [table, sections]);

  // A server-side field error (e.g. duplicate table number) arrives after the save
  // attempt: show it on the field and put the cursor back in it.
  useEffect(() => {
    if (!serverError?.field) return;

    setErrors(prev => ({ ...prev, [serverError.field]: serverError.message }));

    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-field="${serverError.field}"]`);
      el?.focus();
      el?.select?.();
    }, 50);
    return () => clearTimeout(timer);
  }, [serverError]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
      // Let the parent drop the server error too, so it can't be re-applied.
      onClearServerError?.();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Catch a duplicate number before the round-trip. Only fires on an exact
    // case-insensitive hit in the loaded list (excluding the table being edited),
    // so it cannot false-positive; the API still rejects what it misses.
    const trimmedNumber = formData.number.trim();
    const duplicate = existingTables.find(
      (existing) =>
        (existing?.tableNumber ?? existing?.number ?? '').trim().toLowerCase() ===
          trimmedNumber.toLowerCase() && existing.id !== table?.id
    );

    if (duplicate) {
      setErrors(prev => ({
        ...prev,
        number: `A table numbered "${duplicate.tableNumber ?? duplicate.number}" already exists. Please use a different number.`,
      }));
      setTimeout(() => {
        const el = document.querySelector('[data-field="number"]');
        el?.focus();
        el?.select?.();
      }, 50);
      return;
    }

    const dataToSave = {
      // Send the trimmed number — the API trims before its duplicate check.
      number: trimmedNumber,
      section: formData.section,
      sectionId: formData.sectionId,
      sectionName: formData.section, 
      capacity: parseInt(formData.capacity) || 0,
      description: formData.description,
      isActive: formData.isActive,
      tableShape: formData.tableShape,
      position: formData.position
    };

    onSave(dataToSave);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {table ? 'Edit Table' : 'Add Table'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-5">

            {/* Table Information */}
            <div className="grid grid-cols-2 gap-4">
              {/* Table Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Table Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  data-field="number"
                  value={formData.number}
                  onChange={(e) => handleChange('number', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500 ${
                    errors.number ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., 1, A1, T-01"
                  required
                  aria-invalid={!!errors.number}
                />
                {errors.number && (
                  <p className="text-xs text-red-600 mt-1">{errors.number}</p>
                )}
              </div>

              {/*  Section dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Section <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.sectionId}
                  onChange={(e) => {
                    const selectedSection = sections.find(s => s.id === parseInt(e.target.value));
                    handleChange('sectionId', parseInt(e.target.value));
                    handleChange('section', selectedSection?.name || '');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                  required
                >
                  {sections.map(section => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Capacity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Capacity (Seats) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => handleChange('capacity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="e.g., 4"
                  min="1"
                  required
                />
              </div>

              {/* Shape */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Table Shape
                </label>
                <select
                  value={formData.tableShape}
                  onChange={(e) => handleChange('tableShape', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="square">Square</option>
                  <option value="round">Round</option>
                  {/* <option value="rectangle">Rectangle</option> */}
                </select>
              </div>

              {/* Status */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Status
                </label>
                <select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => handleChange('isActive', e.target.value === 'active')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Optional notes about this table"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors text-sm"
            >
              {table ? 'Update Table' : 'Add Table'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}